import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import React from "react";
import FormDateWrapper from "../modals/CoreModal/FormDateWrapper";
import { FormInputWrapper } from "../modals/CoreModal/FormInputWrapper";
import {
  FormSelectWrapper,
  SelectOption,
} from "../modals/CoreModal/FormSelectWrapper";
import styles from "./styles.module.scss";

interface PurchaseProduct {
  asin: string;
  title: string;
  sku: string;
  cost: string;
  quantity: string;
}

interface Purchase {
  supplierName: string;
  supplierId: string;
  orderNumber: string;
  status: string;
  orderDate: Date | null;
  estimatedCompletionDate: Date | null;
  actualCompletionDate: Date | null;
  products: PurchaseProduct[];
}

export interface PurchaseFormOptions {
  suppliers?: SelectOption[];
  status?: SelectOption[];
}

export interface PurchaseFormData extends Purchase {}

interface PurchaseFormProps {
  data: PurchaseFormData;
  onChange: (d: PurchaseFormData) => void;
  options?: PurchaseFormOptions;
}

const DEFAULT_SUPPLIERS: SelectOption[] = [
  { id: "Supplier C1", text: "Supplier C1" },
  { id: "Supplier C2", text: "Supplier C2" },
  { id: "Supplier C3", text: "Supplier C3" },
];

const DEFAULT_STATUS: SelectOption[] = [
  { id: "Open", text: "Open" },
  { id: "Closed", text: "Closed" },
];

const PurchaseForm: React.FC<PurchaseFormProps> = ({
  data,
  onChange,
  options = {},
}) => {
  const setField = <
    K extends keyof PurchaseFormData,
    V extends PurchaseFormData[K],
  >(
    key: K,
    value: V,
  ) => onChange({ ...data, [key]: value });

  const setProductField = (
    idx: number,
    key: keyof PurchaseProduct,
    value: string,
  ) => {
    const updated = data.products.map((p, i) =>
      i === idx ? { ...p, [key]: value } : p,
    );
    onChange({ ...data, products: updated });
  };

  const addProduct = () =>
    onChange({
      ...data,
      products: [
        ...data.products,
        { asin: "", title: "", sku: "", cost: "", quantity: "" },
      ],
    });

  const removeProduct = (idx: number) =>
    onChange({ ...data, products: data.products.filter((_, i) => i !== idx) });

  const supplierOpts = options.suppliers ?? DEFAULT_SUPPLIERS;
  const statusOpts = options.status ?? DEFAULT_STATUS;

  return (
    <>
      <div className={styles.row}>
        <FormSelectWrapper
          label="Supplier Name"
          placeholder="Select supplier"
          value={data.supplierName}
          onChange={(v) => setField("supplierName", v)}
          options={supplierOpts}
        />
        <FormInputWrapper
          label="Supplier ID"
          placeholder="e.g. id_123qwe"
          value={data.supplierId}
          onChange={(v) => setField("supplierId", v)}
        />
      </div>

      <div className={styles.row}>
        <FormInputWrapper
          label="Order Number"
          placeholder="Enter order number"
          value={data.orderNumber}
          onChange={(v) => setField("orderNumber", v)}
        />
        <FormSelectWrapper
          label="Status"
          placeholder="Select status"
          value={data.status}
          onChange={(v) => setField("status", v)}
          options={statusOpts}
        />
      </div>

      <div className={styles.row}>
        <FormDateWrapper
          label="Order Date"
          onChange={(d) => setField("orderDate", d)}
        />
        <FormDateWrapper
          label="Estimated Completion Date"
          optional
          onChange={(d) => setField("estimatedCompletionDate", d)}
        />
        <FormDateWrapper
          label="Actual Completion Date"
          optional
          onChange={(d) => setField("actualCompletionDate", d)}
        />
      </div>

      <div className={styles.wrapperRow}>
        {data.products.map((p, idx) => (
          <div key={idx} className={styles.row}>
            <FormInputWrapper
              label={idx === 0 ? "ASIN" : undefined}
              value={p.asin}
              onChange={(v) => setProductField(idx, "asin", v)}
            />
            <FormInputWrapper
              label={idx === 0 ? "Product Title" : undefined}
              value={p.title}
              onChange={(v) => setProductField(idx, "title", v)}
            />
            <FormInputWrapper
              label={idx === 0 ? "SKU" : undefined}
              value={p.sku}
              onChange={(v) => setProductField(idx, "sku", v)}
            />
            <FormInputWrapper
              label={idx === 0 ? "COGS per unit (£)" : undefined}
              value={p.cost}
              onChange={(v) => setProductField(idx, "cost", v)}
            />
            <FormInputWrapper
              label={idx === 0 ? "Quantity" : undefined}
              value={p.quantity}
              type="number"
              onChange={(v) => setProductField(idx, "quantity", v)}
            />

            <button
              className={styles.removeBtn}
              onClick={() => removeProduct(idx)}
            >
              <CloseOutlined />
            </button>
          </div>
        ))}
      </div>

      <button className={styles.action} onClick={addProduct}>
        <PlusOutlined /> <span>Add ASIN</span>
      </button>
    </>
  );
};

export default PurchaseForm;
